import { aiChatApi, guidelinesApi } from 'app/services/api';
import { aiChatSignalRController } from 'app/services/signalR';
import _ from 'lodash';

import {
  AI_CHAT_ACTION_TYPE,
  AI_CHAT_FEEDBACK_ERROR_MESSAGE,
  AiChatMessageRole,
  AiChatMessageStatus,
  GUIDELINE_UNAVAILABLE_MESSAGE,
  PLAN_REVISION_FLAG_TIMEOUT_MS,
} from 'constants/aiChat';
import { HTTP_RESPONSE_STATUS_CODES } from 'constants/http';
import { catchAsyncActionError } from 'utils/action';
import { logger } from 'utils/logger';
import { getUUID } from 'utils/uuid';

import { selectMasterCompanyId } from 'store/auth/selectors';
import { showMessageAction } from 'store/global/actions/messages.actions';
import { selectCurrentUserLoanRoleIds } from 'store/loanApplications/selectors/loanRoles.selectors';

import {
  selectAiChatLoanId,
  selectAiChatMessageOutcomeByMessageId,
  selectAiChatSessionId,
} from '../selectors';

export const getSessionAction =
  ({ loanId }) =>
  async dispatch => {
    dispatch({ type: AI_CHAT_ACTION_TYPE.GET_SESSION });

    try {
      const { data } = await aiChatApi.getSession({ loanId });

      dispatch({
        type: AI_CHAT_ACTION_TYPE.GET_SESSION_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      dispatch({
        type: AI_CHAT_ACTION_TYPE.GET_SESSION_FAILURE,
        payload: error.message,
      });

      return catchAsyncActionError(error);
    }
  };

export const getMessagesAction =
  ({ loanId, limit, before }) =>
  async dispatch => {
    dispatch({ type: AI_CHAT_ACTION_TYPE.GET_MESSAGES, payload: { loanId } });

    try {
      const { data } = await aiChatApi.getMessages({ loanId, limit, before });

      dispatch({
        type: AI_CHAT_ACTION_TYPE.GET_MESSAGES_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      dispatch({
        type: AI_CHAT_ACTION_TYPE.GET_MESSAGES_FAILURE,
        payload: error.message,
      });

      return catchAsyncActionError(error);
    }
  };

// DEV-4392: re-fetch chat history after a SignalR reconnect. SignalR does not
// replay missed events, so a question/confirmation that paused the turn during
// the connection gap is recovered only by refreshing the messages — the
// projected `hasPendingQuestions` flag then re-fires the AiChatPanel rehydrate
// effect, which loads the parked question and renders the card. loanId is read
// from the active session so the connector doesn't need to carry it.
export const refreshChatMessagesAction = () => async (dispatch, getState) => {
  const loanId = selectAiChatLoanId(getState());

  if (_.isNil(loanId)) {
    return null;
  }

  return dispatch(getMessagesAction({ loanId }));
};

export const sendMessageAction =
  ({ loanId, applicationId, content }) =>
  async (dispatch, getState) => {
    const sessionId = selectAiChatSessionId(getState());
    const clientTempId = getUUID();
    const optimisticUserMessage = {
      id: clientTempId,
      sessionId: sessionId ?? null,
      role: AiChatMessageRole.USER,
      status: AiChatMessageStatus.SENT,
      content,
      timestamp: new Date().toISOString(),
    };

    dispatch({
      type: AI_CHAT_ACTION_TYPE.ADD_MESSAGE,
      payload: optimisticUserMessage,
    });

    dispatch({ type: AI_CHAT_ACTION_TYPE.SEND_MESSAGE });

    try {
      const { data } = await aiChatApi.sendMessage({ loanId, applicationId, content });

      dispatch({
        type: AI_CHAT_ACTION_TYPE.SEND_MESSAGE_SUCCESS,
        payload: data,
        meta: { clientTempId },
      });

      return data;
    } catch (error) {
      dispatch({
        type: AI_CHAT_ACTION_TYPE.SEND_MESSAGE_FAILURE,
        payload: error.message,
        meta: { clientTempId },
      });

      return catchAsyncActionError(error);
    }
  };

const cancelMessageBestEffort = async ({ loanId, messageId }) => {
  try {
    await aiChatApi.cancelMessage({ loanId, messageId });
  } catch (error) {
    logger.warn('[AiChat] Best-effort cancel before retry did not complete cleanly', {
      loanId,
      messageId,
      error: error?.message,
    });
  }
};

export const retryMessageAction =
  ({ loanId, applicationId, messageId }) =>
  async dispatch => {
    await cancelMessageBestEffort({ loanId, messageId });

    dispatch({
      type: AI_CHAT_ACTION_TYPE.RETRY_MESSAGE,
      payload: { messageId },
    });

    try {
      const { data } = await aiChatApi.retryMessage({ loanId, applicationId, messageId });

      dispatch({
        type: AI_CHAT_ACTION_TYPE.RETRY_MESSAGE_SUCCESS,
        payload: data,
        meta: { messageId },
      });

      return data;
    } catch (error) {
      dispatch({
        type: AI_CHAT_ACTION_TYPE.RETRY_MESSAGE_FAILURE,
        payload: error.message,
        meta: { messageId },
      });

      return catchAsyncActionError(error);
    }
  };

export const setAssistantSlowAction = isSlow => ({
  type: AI_CHAT_ACTION_TYPE.SET_ASSISTANT_SLOW,
  payload: !!isSlow,
});

export const setMessageFeedbackAction =
  ({ messageId, outcome }) =>
  async (dispatch, getState) => {
    if (_.isNil(messageId) || _.isNil(outcome)) {
      return null;
    }

    const previousOutcome = selectAiChatMessageOutcomeByMessageId(getState(), messageId);

    dispatch({
      type: AI_CHAT_ACTION_TYPE.SET_MESSAGE_FEEDBACK,
      payload: { messageId, outcome },
    });

    try {
      await aiChatApi.submitMessageFeedback({ messageId, outcome });

      return outcome;
    } catch (error) {
      dispatch({
        type: AI_CHAT_ACTION_TYPE.SET_MESSAGE_FEEDBACK_FAILURE,
        payload: { messageId, outcome: previousOutcome },
      });
      dispatch(
        showMessageAction({
          variant: 'error',
          message: AI_CHAT_FEEDBACK_ERROR_MESSAGE,
        }),
      );

      return catchAsyncActionError(error);
    }
  };

export const addMessageAction = message => ({
  type: AI_CHAT_ACTION_TYPE.ADD_MESSAGE,
  payload: message,
});

export const updateMessageAction = message => ({
  type: AI_CHAT_ACTION_TYPE.UPDATE_MESSAGE,
  payload: message,
});

export const setIsLoadingAction = isLoading => ({
  type: AI_CHAT_ACTION_TYPE.SET_IS_LOADING,
  payload: isLoading,
});

export const setIsSendingAction = isSending => ({
  type: AI_CHAT_ACTION_TYPE.SET_IS_SENDING,
  payload: isSending,
});

export const setErrorAction = error => ({
  type: AI_CHAT_ACTION_TYPE.SET_ERROR,
  payload: error,
});

export const clearErrorAction = () => ({
  type: AI_CHAT_ACTION_TYPE.CLEAR_ERROR,
});

export const resetAiChatStateAction = () => ({
  type: AI_CHAT_ACTION_TYPE.RESET_STATE,
});

export const setIsPanelOpenAction = isOpen => ({
  type: AI_CHAT_ACTION_TYPE.SET_IS_PANEL_OPEN,
  payload: isOpen,
});

export const setIsConnectedAction = isConnected => ({
  type: AI_CHAT_ACTION_TYPE.SET_IS_CONNECTED,
  payload: isConnected,
});

export const setExecutionPlanAction = plan => ({
  type: AI_CHAT_ACTION_TYPE.SET_EXECUTION_PLAN,
  payload: plan,
});

export const updateExecutionPlanStepAction = step => ({
  type: AI_CHAT_ACTION_TYPE.UPDATE_EXECUTION_PLAN_STEP,
  payload: step,
});

export const clearExecutionPlanAction = () => ({
  type: AI_CHAT_ACTION_TYPE.CLEAR_EXECUTION_PLAN,
});

export const cancelExecutionAction =
  ({ loanId, messageId }) =>
  async () => {
    if (_.isNil(loanId) || _.isNil(messageId)) {
      return null;
    }

    try {
      await aiChatApi.cancelMessage({ loanId, messageId });
    } catch (error) {
      return catchAsyncActionError(error);
    }

    return null;
  };

export const sessionStoppedAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.SESSION_STOPPED,
  payload,
});

export const stopChatSessionAction =
  ({ loanId }) =>
  async dispatch => {
    if (_.isNil(loanId)) {
      return null;
    }

    dispatch({ type: AI_CHAT_ACTION_TYPE.STOP_SESSION });

    try {
      const { data } = await aiChatApi.stopSession({ loanId });

      dispatch({
        type: AI_CHAT_ACTION_TYPE.STOP_SESSION_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      dispatch({
        type: AI_CHAT_ACTION_TYPE.STOP_SESSION_FAILURE,
        payload: error.message,
      });

      return catchAsyncActionError(error);
    }
  };

export const setGuidelineFullViewAction = citation => ({
  type: AI_CHAT_ACTION_TYPE.SET_GUIDELINE_FULL_VIEW,
  payload: citation,
});

export const cacheGuidelineContentAction = ({ id, content }) => ({
  type: AI_CHAT_ACTION_TYPE.CACHE_GUIDELINE_CONTENT,
  payload: { id, content },
});

export const clearGuidelineCacheAction = () => ({
  type: AI_CHAT_ACTION_TYPE.CLEAR_GUIDELINE_CACHE,
});

export const fetchGuidelineContentAction =
  ({ citationId, sourceId, sectionNumber }) =>
  async (dispatch, getState) => {
    const { aiChat } = getState();

    if (aiChat.guidelineCache[citationId]) {
      return aiChat.guidelineCache[citationId];
    }

    try {
      const isGuid =
        _.isString(citationId) &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(citationId);

      const { data } =
        isGuid || !sourceId || !sectionNumber
          ? await guidelinesApi.getSectionById(citationId)
          : await guidelinesApi.lookupSection({ sourceId, sectionNumber });

      dispatch(cacheGuidelineContentAction({ id: citationId, content: data.content }));

      return data.content;
    } catch (error) {
      dispatch({
        type: AI_CHAT_ACTION_TYPE.FETCH_GUIDELINE_CONTENT_FAILURE,
        payload: error.message,
      });

      return catchAsyncActionError(error);
    }
  };

export const setPendingToolExecutionAction = toolExecution => ({
  type: AI_CHAT_ACTION_TYPE.SET_PENDING_TOOL_EXECUTION,
  payload: toolExecution,
});

export const clearPendingToolExecutionAction = () => ({
  type: AI_CHAT_ACTION_TYPE.CLEAR_PENDING_TOOL_EXECUTION,
});

export const addToolExecutionResultAction = result => ({
  type: AI_CHAT_ACTION_TYPE.ADD_TOOL_EXECUTION_RESULT,
  payload: result,
});

export const clearToolExecutionHistoryAction = () => ({
  type: AI_CHAT_ACTION_TYPE.CLEAR_TOOL_EXECUTION_HISTORY,
});

export const ensureSessionAction =
  ({ loanId }) =>
  async (dispatch, getState) => {
    const existingSessionId = selectAiChatSessionId(getState());

    if (existingSessionId) {
      return { sessionId: existingSessionId };
    }

    const userLoanRoleIds = selectCurrentUserLoanRoleIds(getState());

    dispatch({ type: AI_CHAT_ACTION_TYPE.ENSURE_SESSION });

    try {
      const { data } = await aiChatApi.getOrCreateSession({ loanId, userLoanRoleIds });

      dispatch({
        type: AI_CHAT_ACTION_TYPE.ENSURE_SESSION_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      dispatch({
        type: AI_CHAT_ACTION_TYPE.ENSURE_SESSION_FAILURE,
        payload: error.message,
      });

      return catchAsyncActionError(error);
    }
  };

export const planPlanningStartedAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.PLAN_PLANNING_STARTED,
  payload,
});

export const planCreatedAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.PLAN_CREATED,
  payload,
});

export const planUpdatedAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.PLAN_UPDATED,
  payload,
});

export const stepStartedAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.STEP_STARTED,
  payload,
});

export const stepCompletedAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.STEP_COMPLETED,
  payload,
});

export const stepFailedAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.STEP_FAILED,
  payload,
});

export const planStepStartedAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.PLAN_STEP_STARTED,
  payload,
});

export const planStepToolDispatchedAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.PLAN_STEP_TOOL_DISPATCHED,
  payload,
});

export const planStepToolReturnedAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.PLAN_STEP_TOOL_RETURNED,
  payload,
});

export const planStepCompletedAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.PLAN_STEP_COMPLETED,
  payload,
});

export const planStepFailedAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.PLAN_STEP_FAILED,
  payload,
});

export const planStepSkippedAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.PLAN_STEP_SKIPPED,
  payload,
});

// DEV-2913: BE fires plan_step_awaiting_confirmation when a bridged
// advisory step's tool is frontend-routed (today only `open_loan_section`).
// Flips the row to the AWAITING_CONFIRMATION badge until the user accepts
// or rejects the confirmation card.
export const planStepAwaitingConfirmationAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.PLAN_STEP_AWAITING_CONFIRMATION,
  payload,
});

// DEV-2913: BE fires plan_step_cancelled on the resume turn when the user
// rejected the FE confirmation card. Distinct from FAILED (no error) and
// SKIPPED (model never invoked the tool).
export const planStepCancelledAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.PLAN_STEP_CANCELLED,
  payload,
});

export const planCompletedAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.PLAN_COMPLETED,
  payload,
});

export const planCancelledAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.PLAN_CANCELLED,
  payload,
});

export const clearPlanRevisionFlagsAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.CLEAR_PLAN_REVISION_FLAGS,
  payload,
});

export const setActionPlanExpansionAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.SET_ACTION_PLAN_EXPANSION,
  payload,
});

const resolvePayloadExecutionId = payload =>
  payload?.executionId ?? payload?.stepExecutionId ?? payload?.messageId ?? null;

export const planRevisedAction = payload => dispatch => {
  dispatch({ type: AI_CHAT_ACTION_TYPE.PLAN_REVISED, payload });

  const executionId = resolvePayloadExecutionId(payload);

  if (_.isNil(executionId)) {
    return;
  }

  setTimeout(() => {
    dispatch(clearPlanRevisionFlagsAction({ executionId }));
  }, PLAN_REVISION_FLAG_TIMEOUT_MS);
};

export const toolStartedAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.TOOL_STARTED,
  payload,
});

export const toolCompletedAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.TOOL_COMPLETED,
  payload,
});

export const toolFailedAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.TOOL_FAILED,
  payload,
});

export const toolConfirmationRequestedAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.TOOL_CONFIRMATION_REQUESTED,
  payload,
});

export const responseTokenAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.RESPONSE_TOKEN,
  payload,
});

export const responseCompleteAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.RESPONSE_COMPLETE,
  payload,
});

export const executionCancelledAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.EXECUTION_CANCELLED,
  payload,
});

export const messageFailedAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.MESSAGE_FAILED,
  payload,
});

export const loadExecutionPlanBlobAction =
  ({ loanId, messageId }) =>
  async dispatch => {
    try {
      const { data } = await aiChatApi.getExecutionPlan({ loanId, messageId });

      if (_.isNil(data)) {
        return null;
      }

      dispatch({
        type: AI_CHAT_ACTION_TYPE.PLAN_CREATED,
        payload: { ...data, messageId },
      });

      return data;
    } catch (error) {
      if (isNotFoundError(error)) {
        logger.warn('[AiChat] Execution plan blob endpoint not available yet', {
          loanId,
          messageId,
        });

        return null;
      }

      return catchAsyncActionError(error);
    }
  };

export const citationsExtractedAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.CITATIONS_EXTRACTED,
  payload,
});

const isNotFoundError = error => error?.response?.status === HTTP_RESPONSE_STATUS_CODES.NOT_FOUND;

export const loadCitationsBlobAction =
  ({ loanId, messageId }) =>
  async dispatch => {
    try {
      const { data } = await aiChatApi.getCitationsBlob({ loanId, messageId });
      const citations = Array.isArray(data) ? data : (data?.citations ?? []);

      dispatch({
        type: AI_CHAT_ACTION_TYPE.CITATIONS_BLOB_LOADED,
        payload: { messageId, citations },
      });

      return citations;
    } catch (error) {
      if (isNotFoundError(error)) {
        logger.warn('[AiChat] Citations blob endpoint not available yet', {
          loanId,
          messageId,
        });

        return null;
      }

      return catchAsyncActionError(error);
    }
  };

export const messageReceivedAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.MESSAGE_RECEIVED,
  payload,
});

export const messageContentBlockAddedAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.MESSAGE_CONTENT_BLOCK_ADDED,
  payload,
});

export const messageContentBlockUpdatedAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.MESSAGE_CONTENT_BLOCK_UPDATED,
  payload,
});

export const loadMessageContentBlocksAction =
  ({ loanId, messageId }) =>
  async dispatch => {
    try {
      const { data } = await aiChatApi.getMessageContentBlocks({ loanId, messageId });
      const blocks = Array.isArray(data) ? data : (data?.blocks ?? []);

      dispatch({
        type: AI_CHAT_ACTION_TYPE.MESSAGE_CONTENT_BLOCKS_LOADED,
        payload: { messageId, blocks },
      });

      return blocks;
    } catch (error) {
      if (isNotFoundError(error)) {
        logger.warn('[AiChat] Message content blocks endpoint not available yet', {
          loanId,
          messageId,
        });

        return null;
      }

      return catchAsyncActionError(error);
    }
  };

export const loadUnreadCountAction =
  ({ loanId }) =>
  async dispatch => {
    dispatch({ type: AI_CHAT_ACTION_TYPE.LOAD_UNREAD_COUNT });

    try {
      const { data } = await aiChatApi.getUnreadCount({ loanId });
      const unreadCount = data?.unreadCount ?? 0;

      dispatch({
        type: AI_CHAT_ACTION_TYPE.LOAD_UNREAD_COUNT_SUCCESS,
        payload: { unreadCount },
      });

      return unreadCount;
    } catch (error) {
      dispatch({
        type: AI_CHAT_ACTION_TYPE.LOAD_UNREAD_COUNT_FAILURE,
        payload: error.message,
      });

      return catchAsyncActionError(error);
    }
  };

export const markChatReadAction =
  ({ loanId, lastReadAt }) =>
  async dispatch => {
    dispatch({ type: AI_CHAT_ACTION_TYPE.MARK_CHAT_READ });

    const apiArgs = _.isNil(lastReadAt) ? { loanId } : { loanId, lastReadAt };

    try {
      await aiChatApi.markChatRead(apiArgs);

      dispatch({ type: AI_CHAT_ACTION_TYPE.MARK_CHAT_READ_SUCCESS });

      return true;
    } catch (error) {
      dispatch({
        type: AI_CHAT_ACTION_TYPE.MARK_CHAT_READ_FAILURE,
        payload: error.message,
      });

      return catchAsyncActionError(error);
    }
  };

export const loadEligibleConfigurationsAction =
  ({ loanId }) =>
  async dispatch => {
    dispatch({ type: AI_CHAT_ACTION_TYPE.LOAD_ELIGIBLE_CONFIGURATIONS });

    try {
      const { data } = await aiChatApi.getEligibleConfigurations({ loanId });
      const configurations = data?.configurations ?? data ?? [];

      dispatch({
        type: AI_CHAT_ACTION_TYPE.LOAD_ELIGIBLE_CONFIGURATIONS_SUCCESS,
        payload: { configurations },
      });

      return configurations;
    } catch (error) {
      dispatch({
        type: AI_CHAT_ACTION_TYPE.LOAD_ELIGIBLE_CONFIGURATIONS_FAILURE,
        payload: error.message,
      });

      return catchAsyncActionError(error);
    }
  };

export const switchConfigurationAction =
  ({ loanId, configurationId }) =>
  async (dispatch, getState) => {
    const userLoanRoleIds = selectCurrentUserLoanRoleIds(getState());

    dispatch({ type: AI_CHAT_ACTION_TYPE.SWITCH_CONFIGURATION });

    try {
      const { data } = await aiChatApi.getOrCreateSession({
        loanId,
        userLoanRoleIds,
        configurationId,
      });

      dispatch({
        type: AI_CHAT_ACTION_TYPE.SWITCH_CONFIGURATION_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      dispatch({
        type: AI_CHAT_ACTION_TYPE.SWITCH_CONFIGURATION_FAILURE,
        payload: error.message,
      });

      return catchAsyncActionError(error);
    }
  };

export const toolExecutedAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.TOOL_EXECUTED,
  payload,
});

export const toolCancelledAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.TOOL_CANCELLED,
  payload,
});

export const toolConfirmationTimedOutAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.TOOL_CONFIRMATION_TIMEOUT,
  payload,
});

export const approveToolCallAction =
  ({ messageId, toolCallId, selectedSlaGroupId, conditionDecisions = null }) =>
  async (dispatch, getState) => {
    const state = getState();
    const sessionId = selectAiChatSessionId(state);
    const companyId = selectMasterCompanyId(state);

    dispatch({
      type: AI_CHAT_ACTION_TYPE.APPROVE_TOOL_CALL,
      payload: { messageId, toolCallId },
    });

    try {
      await aiChatSignalRController.respondToToolConfirmation({
        sessionId,
        companyId,
        toolCallId,
        approved: true,
        selectedSlaGroupId: selectedSlaGroupId ?? null,
        conditionDecisions: conditionDecisions ?? null,
      });

      return true;
    } catch (error) {
      dispatch({
        type: AI_CHAT_ACTION_TYPE.APPROVE_TOOL_CALL_FAILURE,
        payload: { messageId, toolCallId, error: error?.message ?? null },
      });
      dispatch(
        showMessageAction({
          variant: 'error',
          message: 'Failed to approve the tool call. Please try again.',
        }),
      );

      return catchAsyncActionError(error);
    }
  };

export const rejectToolCallAction =
  ({ messageId, toolCallId, reason }) =>
  async (dispatch, getState) => {
    const state = getState();
    const sessionId = selectAiChatSessionId(state);
    const companyId = selectMasterCompanyId(state);

    dispatch({
      type: AI_CHAT_ACTION_TYPE.REJECT_TOOL_CALL,
      payload: { messageId, toolCallId, reason: reason ?? null },
    });

    try {
      await aiChatSignalRController.respondToToolConfirmation({
        sessionId,
        companyId,
        toolCallId,
        approved: false,
        reason: reason ?? null,
      });

      return true;
    } catch (error) {
      dispatch({
        type: AI_CHAT_ACTION_TYPE.REJECT_TOOL_CALL_FAILURE,
        payload: { messageId, toolCallId, error: error?.message ?? null },
      });
      dispatch(
        showMessageAction({
          variant: 'error',
          message: 'Failed to decline the tool call. Please try again.',
        }),
      );

      return catchAsyncActionError(error);
    }
  };

export const loadPendingToolConfirmationsAction =
  ({ loanId, messageId }) =>
  async dispatch => {
    dispatch({
      type: AI_CHAT_ACTION_TYPE.LOAD_PENDING_TOOL_CONFIRMATIONS,
      payload: { messageId },
    });

    try {
      const { data } = await aiChatApi.getPendingToolConfirmations({ loanId, messageId });
      const confirmations = data?.confirmations ?? data ?? [];

      dispatch({
        type: AI_CHAT_ACTION_TYPE.LOAD_PENDING_TOOL_CONFIRMATIONS_SUCCESS,
        payload: { messageId, confirmations },
      });

      return confirmations;
    } catch (error) {
      if (isNotFoundError(error)) {
        logger.warn('[AiChat] Pending tool confirmations endpoint not available yet', {
          loanId,
          messageId,
        });

        return null;
      }

      dispatch({
        type: AI_CHAT_ACTION_TYPE.LOAD_PENDING_TOOL_CONFIRMATIONS_FAILURE,
        payload: { messageId, error: error.message },
      });

      return catchAsyncActionError(error);
    }
  };

export const userQuestionRequestedAction = payload => ({
  type: AI_CHAT_ACTION_TYPE.USER_QUESTION_REQUESTED,
  payload,
});

export const respondToUserQuestionAction =
  ({ messageId, questionId, answersJson }) =>
  async (dispatch, getState) => {
    const state = getState();
    const sessionId = selectAiChatSessionId(state);
    const companyId = selectMasterCompanyId(state);

    dispatch({
      type: AI_CHAT_ACTION_TYPE.RESPOND_TO_USER_QUESTION,
      payload: { messageId, questionId },
    });

    try {
      await aiChatSignalRController.respondToUserQuestion({
        sessionId,
        companyId,
        questionId,
        answersJson: answersJson ?? null,
        dismissed: false,
      });

      dispatch({
        type: AI_CHAT_ACTION_TYPE.RESPOND_TO_USER_QUESTION_SUCCESS,
        payload: { messageId, questionId },
      });

      return true;
    } catch (error) {
      dispatch({
        type: AI_CHAT_ACTION_TYPE.RESPOND_TO_USER_QUESTION_FAILURE,
        payload: { messageId, questionId, error: error?.message ?? null },
      });
      dispatch(
        showMessageAction({
          variant: 'error',
          message: 'Failed to submit your answer. Please try again.',
        }),
      );

      return catchAsyncActionError(error);
    }
  };

export const dismissUserQuestionAction =
  ({ messageId, questionId }) =>
  async (dispatch, getState) => {
    const state = getState();
    const sessionId = selectAiChatSessionId(state);
    const companyId = selectMasterCompanyId(state);

    dispatch({
      type: AI_CHAT_ACTION_TYPE.DISMISS_USER_QUESTION,
      payload: { messageId, questionId },
    });

    try {
      await aiChatSignalRController.respondToUserQuestion({
        sessionId,
        companyId,
        questionId,
        answersJson: null,
        dismissed: true,
      });

      dispatch({
        type: AI_CHAT_ACTION_TYPE.DISMISS_USER_QUESTION_SUCCESS,
        payload: { messageId, questionId },
      });

      return true;
    } catch (error) {
      dispatch({
        type: AI_CHAT_ACTION_TYPE.DISMISS_USER_QUESTION_FAILURE,
        payload: { messageId, questionId, error: error?.message ?? null },
      });
      dispatch(
        showMessageAction({
          variant: 'error',
          message: 'Failed to dismiss the question. Please try again.',
        }),
      );

      return catchAsyncActionError(error);
    }
  };

export const loadPendingQuestionsAction =
  ({ loanId, messageId }) =>
  async dispatch => {
    dispatch({
      type: AI_CHAT_ACTION_TYPE.LOAD_PENDING_QUESTIONS,
      payload: { messageId },
    });

    try {
      const { data } = await aiChatApi.getPendingQuestions({ loanId, messageId });
      const questions = data?.questions ?? data ?? [];

      dispatch({
        type: AI_CHAT_ACTION_TYPE.LOAD_PENDING_QUESTIONS_SUCCESS,
        payload: { messageId, questions },
      });

      return questions;
    } catch (error) {
      if (isNotFoundError(error)) {
        logger.warn('[AiChat] Pending questions endpoint not available yet', {
          loanId,
          messageId,
        });

        return null;
      }

      dispatch({
        type: AI_CHAT_ACTION_TYPE.LOAD_PENDING_QUESTIONS_FAILURE,
        payload: { messageId, error: error.message },
      });

      return catchAsyncActionError(error);
    }
  };

export const loadGuidelineAction =
  ({ loanId, messageId, citationId }) =>
  async dispatch => {
    try {
      const { data } = await aiChatApi.getGuidelineBlob({ loanId, messageId, citationId });

      dispatch(cacheGuidelineContentAction({ id: citationId, content: data?.content ?? null }));

      return data;
    } catch (error) {
      if (isNotFoundError(error)) {
        dispatch(showMessageAction({ variant: 'warning', message: GUIDELINE_UNAVAILABLE_MESSAGE }));

        return null;
      }

      return catchAsyncActionError(error);
    }
  };
